import React, { useState, useMemo, useCallback } from 'react';
import { OKRState, Task } from '../types';
import { computeSmartScore, taskStatusConfig, priorityColors, priorityLabels } from '../helpers';
import { useOKRPermissions } from '../useOKRPermissions';
import Card from 'components/card';
import { Zap, Calendar, CheckCircle2, AlertTriangle, Clock, Sun } from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

const isToday = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    return d.toDateString() === now.toDateString();
};

const isThisWeek = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return d >= monday && d <= sunday;
};

const isOverdue = (dateStr: string) => new Date(dateStr) < new Date();

// ── Focus task card ───────────────────────────────────────────────────────────

const FocusCard: React.FC<{
    task: Task;
    rank: number;
    onDone: (id: string) => void;
    onStart: (id: string) => void;
}> = ({ task, rank, onDone, onStart }) => {
    const score = computeSmartScore(task);
    const cfg = taskStatusConfig[task.status];
    const overdue = isOverdue(task.dueDate) && task.status !== 'done';
    const today = isToday(task.dueDate);

    return (
        <div className={`rounded-xl border p-4 flex items-start gap-4 transition-all ${task.status === 'done' ? 'opacity-50 border-gray-100 bg-gray-50' : overdue ? 'border-red-200 bg-red-50/30' : 'border-gray-100 bg-white hover:shadow-sm'}`}>
            {/* Rank */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-extrabold flex-shrink-0 ${rank === 1 ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-400'}`}>
                {task.status === 'done' ? <CheckCircle2 size={16} className="text-green-500" /> : rank}
            </div>

            <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${task.status === 'done' ? 'line-through text-gray-400' : 'text-navy-700'}`}>{task.title}</p>
                {task.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{task.description}</p>}

                <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${priorityColors[task.priority]}`}>{priorityLabels[task.priority]}</span>
                    <span className={`text-xs flex items-center gap-1 ${overdue ? 'text-red-500 font-medium' : today ? 'text-orange-500 font-medium' : 'text-gray-400'}`}>
                        <Calendar size={11} />
                        {overdue ? 'En retard — ' : today ? "Aujourd'hui — " : ''}
                        {new Date(task.dueDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                    </span>
                    {task.estimatedHours > 0 && (
                        <span className="text-xs text-gray-400 flex items-center gap-1"><Clock size={11} />{task.estimatedHours}h est.</span>
                    )}
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-lg ${score >= 15 ? 'text-red-600 bg-red-50' : score >= 8 ? 'text-orange-500 bg-orange-50' : 'text-gray-500 bg-gray-50'}`}>
                        <Zap size={10} className="inline mr-0.5" />{score}
                    </span>
                </div>

                {task.status === 'blocked' && task.blockedReason && (
                    <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-red-50 p-2 text-xs text-red-600">
                        <AlertTriangle size={11} className="flex-shrink-0 mt-0.5" />{task.blockedReason}
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-1.5 flex-shrink-0">
                {task.status === 'todo' && (
                    <button
                        onClick={() => onStart(task.id)}
                        className="text-xs font-semibold text-toola-500 hover:text-toola-600 bg-blue-50 hover:bg-blue-100 border border-toola-200 rounded-lg px-3 py-1.5 transition-colors whitespace-nowrap"
                    >
                        ▶ Démarrer
                    </button>
                )}
                {task.status === 'in-progress' && (
                    <button
                        onClick={() => onDone(task.id)}
                        className="text-xs font-semibold text-green-600 hover:text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg px-3 py-1.5 transition-colors whitespace-nowrap"
                    >
                        ✓ Terminer
                    </button>
                )}
                {task.status === 'done' && (
                    <span className="text-xs text-green-500 font-medium flex items-center gap-1"><CheckCircle2 size={12} /> Fait</span>
                )}
            </div>
        </div>
    );
};

// ── EOD Summary ───────────────────────────────────────────────────────────────

const EODSummary: React.FC<{ tasks: Task[]; memberName: string }> = ({ tasks, memberName }) => {
    const done = tasks.filter((t) => t.status === 'done');
    const slipped = tasks.filter((t) => t.status !== 'done' && isOverdue(t.dueDate));
    const inProgress = tasks.filter((t) => t.status === 'in-progress');

    return (
        <Card extra="p-5 border border-toola-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <h4 className="font-bold text-navy-700 mb-3 flex items-center gap-2">
                <Sun size={16} className="text-orange-400" /> Fin de journée — {memberName}
            </h4>
            <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center rounded-xl bg-white p-3 border border-gray-100">
                    <p className="text-2xl font-extrabold text-green-500">{done.length}</p>
                    <p className="text-xs text-gray-500">Terminée{done.length > 1 ? 's' : ''}</p>
                </div>
                <div className="text-center rounded-xl bg-white p-3 border border-gray-100">
                    <p className="text-2xl font-extrabold text-orange-400">{inProgress.length}</p>
                    <p className="text-xs text-gray-500">En cours</p>
                </div>
                <div className="text-center rounded-xl bg-white p-3 border border-gray-100">
                    <p className="text-2xl font-extrabold text-red-400">{slipped.length}</p>
                    <p className="text-xs text-gray-500">Glissée{slipped.length > 1 ? 's' : ''}</p>
                </div>
            </div>
            {slipped.length > 0 && (
                <div className="text-xs text-red-500">
                    <p className="font-semibold mb-1">Tâches glissées :</p>
                    {slipped.map((t) => <p key={t.id} className="flex items-center gap-1"><AlertTriangle size={10} /> {t.title}</p>)}
                </div>
            )}
        </Card>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const FocusMode: React.FC<{
    state: OKRState;
    onStatusChange: (id: string, s: Task['status']) => void;
}> = ({ state, onStatusChange }) => {
    const { isOwner, currentUserId, currentUserName } = useOKRPermissions();

    // Non-owners are locked to their own tasks
    const defaultMemberId = isOwner
        ? (state.team[0]?.id ?? '')
        : (state.team.find((m) => m.id === currentUserId || m.name === currentUserName)?.id ?? state.team[0]?.id ?? '');

    const [selectedMemberId, setSelectedMemberId] = useState<string>(defaultMemberId);
    const [showEOD, setShowEOD] = useState(false);

    const member = state.team.find((m) => m.id === selectedMemberId);

    const myTasks = useMemo(() =>
        state.tasks.filter((t) => t.assigneeId === selectedMemberId),
        [state.tasks, selectedMemberId],
    );

    // Top tasks: not done, sorted by smart_score DESC, then proximity to today
    const focusTasks = useMemo(() => {
        return [...myTasks]
            .filter((t) => t.status !== 'done')
            .sort((a, b) => {
                const scoreDiff = computeSmartScore(b) - computeSmartScore(a);
                if (Math.abs(scoreDiff) > 2) return scoreDiff;
                return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
            })
            .slice(0, 5);
    }, [myTasks]);

    const todayTasks = myTasks.filter((t) => isToday(t.dueDate));
    const weekTasks = myTasks.filter((t) => isThisWeek(t.dueDate));
    const doneTasks = myTasks.filter((t) => t.status === 'done');

    const handleStart = useCallback((id: string) => onStatusChange(id, 'in-progress'), [onStatusChange]);
    const handleDone = useCallback((id: string) => onStatusChange(id, 'done'), [onStatusChange]);

    if (state.team.length === 0) {
        return (
            <Card extra="p-8 text-center">
                <p className="text-gray-400 font-medium">Aucun membre dans l'équipe</p>
            </Card>
        );
    }

    return (
        <div className="space-y-5">
            {/* Member selector */}
            <Card extra="p-5">
                <div className="flex items-center gap-4 flex-wrap">
                    <Sun size={20} className="text-orange-400 flex-shrink-0" />
                    <div className="flex-1">
                        <h3 className="text-base font-bold text-navy-700">Mode Focus — Ma journée</h3>
                        <p className="text-sm text-gray-500">Top 5 tâches triées par smart score + échéance</p>
                    </div>
                    {isOwner ? (
                        <select
                            value={selectedMemberId}
                            onChange={(e) => setSelectedMemberId(e.target.value)}
                            className="border border-gray-200 rounded-xl px-3 py-2 text-sm text-navy-700 focus:outline-none focus:ring-2 focus:ring-toola-400"
                        >
                            {state.team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                    ) : (
                        <span className="text-sm font-semibold text-navy-700">{member?.name}</span>
                    )}
                </div>
            </Card>

            {/* Stats bar */}
            <div className="grid grid-cols-3 gap-3">
                {[
                    { label: "Aujourd'hui", count: todayTasks.length, color: 'text-orange-500 bg-orange-50 border-orange-200' },
                    { label: 'Cette semaine', count: weekTasks.length, color: 'text-toola-500 bg-blue-50 border-toola-200' },
                    { label: 'Terminées', count: doneTasks.length, color: 'text-green-600 bg-green-50 border-green-200' },
                ].map(({ label, count, color }) => (
                    <div key={label} className={`rounded-xl border p-3 text-center ${color}`}>
                        <p className="text-2xl font-extrabold">{count}</p>
                        <p className="text-xs font-medium mt-0.5">{label}</p>
                    </div>
                ))}
            </div>

            {/* Focus tasks */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-navy-700 flex items-center gap-2">
                        <Zap size={15} className="text-orange-400" /> Tâches prioritaires pour {member?.name ?? 'ce membre'}
                    </h4>
                    <button
                        onClick={() => setShowEOD(!showEOD)}
                        className="text-xs font-medium text-gray-500 hover:text-navy-700 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
                    >
                        {showEOD ? 'Masquer le récap' : '🌅 Bilan de fin de journée'}
                    </button>
                </div>

                <div className="space-y-3">
                    {focusTasks.map((task, i) => (
                        <FocusCard
                            key={task.id}
                            task={task}
                            rank={i + 1}
                            onDone={handleDone}
                            onStart={handleStart}
                        />
                    ))}
                    {focusTasks.length === 0 && (
                        <div className="rounded-xl border-2 border-dashed border-gray-100 p-12 text-center">
                            <CheckCircle2 size={40} className="mx-auto mb-3 text-green-300" />
                            <p className="text-gray-400 font-medium">Aucune tâche en attente</p>
                            <p className="text-sm text-gray-300 mt-1">Toutes les tâches sont terminées !</p>
                        </div>
                    )}
                </div>
            </div>

            {/* EOD Summary */}
            {showEOD && member && (
                <EODSummary tasks={weekTasks} memberName={member.name} />
            )}
        </div>
    );
};

export default FocusMode;
